/**
 * PDP Main 2026 — Product media gallery
 *
 * Web component that wires a Swiper main slider to a horizontal thumbnail
 * slider (Figma "Product Page Desktop - Description"). Relies on the global
 * Swiper bundle loaded in layout/theme.liquid.
 *
 * @element pdp-gallery-2026
 */
class PdpGallery2026 extends HTMLElement {
  constructor() {
    super();
    /** @type {any} */ this.mainSlider = null;
    /** @type {any} */ this.thumbSlider = null;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this._onResize = this._debounce(this._onResize.bind(this), 200);
  }

  connectedCallback() {
    if (this.dataset.initialized === 'true') return;
    if (typeof Swiper === 'undefined') {
      // Swiper is deferred globally; retry once it is available.
      window.addEventListener('load', () => this._init(), { once: true });
      return;
    }
    this._init();
  }

  disconnectedCallback() {
    this._destroy();
    window.removeEventListener('resize', this._onResize);
  }

  _init() {
    const mainEl = this.querySelector('.pdp-main-2026__gallery-main');
    if (!mainEl || typeof Swiper === 'undefined') return;

    const thumbsEl = this.querySelector('.pdp-main-2026__gallery-thumbs');
    if (thumbsEl) {
      this.thumbSlider = new Swiper(/** @type {HTMLElement} */ (thumbsEl), {
        slidesPerView: 4,
        spaceBetween: 12,
        watchSlidesProgress: true,
        slideToClickedSlide: true,
        freeMode: { enabled: true, sticky: false },
      });
    }

    this.mainSlider = new Swiper(/** @type {HTMLElement} */ (mainEl), {
      slidesPerView: 1,
      speed: this.reducedMotion ? 0 : 250,
      effect: this.reducedMotion ? 'slide' : 'fade',
      fadeEffect: { crossFade: true },
      pagination: {
        el: this.querySelector('.pdp-main-2026__pagination'),
        clickable: true,
      },
      keyboard: { enabled: true },
      a11y: {
        prevSlideMessage: 'Previous image',
        nextSlideMessage: 'Next image',
      },
      thumbs: this.thumbSlider ? { swiper: this.thumbSlider } : undefined,
    });

    this._setupThumbNav();
    window.addEventListener('resize', this._onResize);
    this.dataset.initialized = 'true';
  }

  _setupThumbNav() {
    const prevBtn = this.querySelector('.pdp-main-2026__thumb-nav--prev');
    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        if (!this.mainSlider) return;
        this.mainSlider.slidePrev();
      });
    }

    const nextBtn = this.querySelector('.pdp-main-2026__thumb-nav--next');
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        if (!this.mainSlider) return;
        this.mainSlider.slideNext();
      });
    }
  }

  _onResize() {
    if (this.mainSlider) this.mainSlider.update();
    if (this.thumbSlider) this.thumbSlider.update();
  }

  _destroy() {
    if (this.mainSlider) {
      this.mainSlider.destroy(true, true);
      this.mainSlider = null;
    }
    if (this.thumbSlider) {
      this.thumbSlider.destroy(true, true);
      this.thumbSlider = null;
    }
    this.dataset.initialized = 'false';
  }

  /**
   * @param {(...args: any[]) => void} fn
   * @param {number} wait
   * @returns {(...args: any[]) => void}
   */
  _debounce(fn, wait) {
    /** @type {number|undefined} */ let t;
    return (...args) => {
      if (t) clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }
}

if (!customElements.get('pdp-gallery-2026')) {
  customElements.define('pdp-gallery-2026', PdpGallery2026);
}

/**
 * PDP Main 2026 — "Write a Review" bridge
 *
 * The header "Write a Review" link (`.pdp-main-2026__meta-write`) lives in this
 * section, but the real review form is opened by Okendo's own button, injected
 * into the reviews widget lower on the page. That button is a plain
 * `<a class="oke-w-writeReview" target="_blank">` that opens the Okendo review
 * form in a new tab — no modal, no JS API. So clicking the header link just
 * forwards the click to Okendo's anchor (mirroring its href, rel and any
 * analytics listeners).
 *
 * The forwarding MUST stay synchronous inside the user's click event: an async
 * `.click()` (e.g. from setTimeout) that opens `target="_blank"` is treated as
 * an unrequested popup and blocked by the browser.
 */
(function () {
  /** Okendo's write-review anchor. Class/layout varies by widget version. */
  var OKE_WRITE_SELECTORS = [
    'a.oke-w-writeReview',
    '.oke-w-reviews-writeReview a',
    'a.oke-button.oke-w-writeReview',
  ];

  function findOkendoWriteAnchor() {
    for (var i = 0; i < OKE_WRITE_SELECTORS.length; i++) {
      var el = document.querySelector(OKE_WRITE_SELECTORS[i]);
      if (el) return el;
    }
    // Last resort: a container element — dig out its inner anchor.
    var container = document.querySelector('.oke-w-reviews-writeReview');
    return container ? container.querySelector('a') : null;
  }

  document.addEventListener('click', function (event) {
    var link = event.target.closest('.pdp-main-2026__meta-write');
    if (!link) return;
    event.preventDefault();

    var anchor = findOkendoWriteAnchor();
    if (anchor) {
      // Synchronous click = same behaviour as clicking Okendo's own button.
      anchor.click();
      return;
    }

    // Okendo widget not present/ready: fall back to the reviews anchor.
    var href = link.getAttribute('href');
    if (href) window.location.hash = href.replace(/^#/, '');
  });
})();
